import "./ReservationSidebar.scss"

import { FC, ReactNode, useEffect, useMemo, useState, useRef, useCallback } from "react"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import dayjs from "utils/dayjs"

import Summary from "components/Summary/Summary"
import NoValue from "components/NoValue/NoValue"
import { Guid, ISummarySection } from "@/types"
import { IListItem, ListItemType } from "components/List/List"
import { applyLineBreakAfterRule, formatNumber, getPurposeLabel } from "utils/utils"
import { InvoicingType, Purpose } from "config/constants"
import Language from "models/Language"
import Reservation, { IReservationPricingSummaryItem, RESERVATION_INVOICE_TEXT_LINE_BREAK_AFTER } from "models/Reservation"
import CustomerNumber from "components/CustomerNumber/CustomerNumber"
import { getInvoicePositionTypeLabel } from "models/InvoicePositionBase"
import ButtonLinkTooltip from "components/ButtonLinkTooltip/ButtonLinkTooltip"
import Text from "components/Text/Text"
import TenantEmployee from "models/TenantEmployee"
import DeliveryTerm from "models/DeliveryTerm"
import PaymentTerm from "models/PaymentTerm"
import OrderType from "models/OrderType"
import Country from "models/Country"
import useUserEmails from "hooks/useUserEmails"
import useReservationCommissionDisplay from "hooks/useReservationCommissionDisplay"
import ProfitCenter from "models/ProfitCenter"
import { ReservationStep } from "pages/ReservationPage/config"
import Routes from "@/routes"
import useWorkingDaysCalendars from "hooks/useWorkingDaysCalendars"
import Input from "components/Input/Input"
import MapView, { MapViewControl } from "components/MapView/MapView"
import { IMapMarkerProps } from "components/MapView/MapMarker/MapMarker"
import Button from "components/Button/Button"
import { Flex } from "components/Flex"
import { useReservationPinnedFields, ReservationField } from "stores/reservationPinnedFields"

export interface IReservationSidebarDetailsProps {
    reservation: Reservation
    rentToBuyRelatedReservation?: Reservation | null
    isLoadingRentToBuyRelatedReservation?: boolean
    language?: Language
    addressWarehouseDescription?: string | null
    warehouseDescription?: ReactNode
    salesPerson?: TenantEmployee | null
    salesAdmin?: TenantEmployee | null
    deliveryTerm?: DeliveryTerm | null
    paymentTerm?: PaymentTerm | null
    orderType?: OrderType | null
    originCountry?: Country | null
    targetCountry?: Country | null
    profitCenter?: ProfitCenter | null
    totalPrices: IReservationPricingSummaryItem[]
    canEdit?: boolean
    handleCommentSave: (comment: string) => void
}

const ReservationSidebarDetails: FC<IReservationSidebarDetailsProps> = ({
    reservation,
    rentToBuyRelatedReservation,
    isLoadingRentToBuyRelatedReservation,
    language,
    addressWarehouseDescription,
    warehouseDescription,
    salesPerson,
    salesAdmin,
    deliveryTerm,
    paymentTerm,
    orderType,
    originCountry,
    targetCountry,
    profitCenter,
    totalPrices,
    canEdit,
    handleCommentSave,
}) => {
    const { t } = useTranslation()
    const location = useLocation()
    const { pinnedFields, toggleFieldPin, fetchPinnedFields } = useReservationPinnedFields()
    useEffect(() => { if (!pinnedFields) fetchPinnedFields() }, [pinnedFields, fetchPinnedFields])

    const noValue = <NoValue />
    const loading = <NoValue>{t('common.loading')}</NoValue>
    const noText = <NoValue>{t('reservation.noText')}</NoValue>

    const currency = reservation.requestCurrency
    const { workingDaysCalendarsMap } = useWorkingDaysCalendars({ autoload: true })
    const { isCommissionDisplayed } = useReservationCommissionDisplay({
        purpose: reservation?.purpose,
        commission: reservation?.commission,
    })

    const userIds = useMemo(
        () =>
            [reservation.rentToBuy.changeAssetTypeLastUserId, reservation.pricingDetails?.invoiceBlockedBy]
                .filter(Boolean) as Guid[],
        [reservation.rentToBuy.changeAssetTypeLastUserId, reservation.pricingDetails?.invoiceBlockedBy]
    )
    const { getEmail } = useUserEmails({ userIds })
    const changeAssetTypeLastUserEmail = getEmail(reservation.rentToBuy.changeAssetTypeLastUserId ?? undefined)
    const invoiceBlockedByEmail = getEmail(reservation.pricingDetails?.invoiceBlockedBy ?? undefined)

    // Comment edit state (context allows safe editing from pinned or section)
    const [editingContext, setEditingContext] = useState<'pinned' | 'section' | null>(null)
    const isEditingComment = editingContext !== null
    const [commentValue, setCommentValue] = useState(reservation.comment || '')
    const skipBlurSaveRef = useRef(false)
    const startEditComment = (context: 'pinned' | 'section') => {
        setEditingContext(context)
        setCommentValue(reservation.comment || '')
    }
    const handleSaveComment = () => {
        setEditingContext(null)
        handleCommentSave(commentValue)
    }
    const handleBlurComment = () => {
        if (skipBlurSaveRef.current) {
            skipBlurSaveRef.current = false
            return
        }
        if (editingContext) handleSaveComment()
    }
    const handleCancelComment = () => {
        setEditingContext(null)
        setCommentValue(reservation.comment || '')
    }
    const onToolbarPointerDown = useCallback(() => {
        skipBlurSaveRef.current = true
    }, [])

    // Map
    const mapMarkers = useMemo<IMapMarkerProps[] | undefined>(() => {
        if (!reservation?.location) return undefined
        return [{ coordinates: { lat: reservation.location.lat, lng: reservation.location.lng } }]
    }, [reservation])
    const renderMap = () => {
        if (!mapMarkers) return null
        return (
            <div className="reservation-sidebar__map mb-3">
                <MapView
                    markers={mapMarkers}
                    autoCenterToMarkers
                    controls={[
                        MapViewControl.type,
                        MapViewControl.zoom,
                    ]}
                />
            </div>
        )
    }

    const isCommentsPinned = pinnedFields?.includes(ReservationField.comments)
    const commentToolbar = (
        // Original section toolbar always available; if editing from pinned, disable buttons
        !isCommentsPinned || (isCommentsPinned && editingContext !== 'pinned') ? (
            !isEditingComment || editingContext !== 'section' ? (
                <Button
                    label={t('common.edit')}
                    theme="link"
                    onClick={() => startEditComment('section')}
                    disabled={editingContext === 'pinned'}
                />
            ) : (
                <div onPointerDown={onToolbarPointerDown}>
                    <Flex gap="0.8rem" alignItems="center">
                        <Button label={t('common.save')} theme="link" onClick={handleSaveComment} />
                        <div className="reservation-sidebar__toolbar-divider" />
                        <Button label={t('common.cancel')} theme="link" onClick={handleCancelComment} />
                    </Flex>
                </div>
            )
        ) : undefined
    )

    const details: ISummarySection<IListItem>[] = []
    // Pinned block duplicates (not removes) rows; order taken from pinnedFields list

    // Request Details (includes editable comments subsection + rent-to-buy subsection)
    const requestDetailsItems: IListItem[] = []

    if (reservation.time.reservationStart && reservation.time.reservationEnd) {
        requestDetailsItems.push({
            label: t('reservation.period'),
            value: reservation.printDate(),
            valueTestId: "reservation-period",
            pinned: pinnedFields?.includes(ReservationField.period),
            onPinnedToggle: () => toggleFieldPin(ReservationField.period),
        })
    } else if (!reservation.time.reservationEnd && reservation.time.reservationStart) {
        requestDetailsItems.push({
            label: t('common.start'),
            value: `${t('scheduler.fromX', { date: reservation.time.reservationStart.format(t('config.dateLongTimeFormat')) })}`,
            valueTestId: "reservation-period",
            pinned: pinnedFields?.includes(ReservationField.period),
            onPinnedToggle: () => toggleFieldPin(ReservationField.period),
        })
    } else {
        requestDetailsItems.push({
            label: t('reservation.period'),
            value: noValue,
            valueTestId: "reservation-period",
            pinned: pinnedFields?.includes(ReservationField.period),
            onPinnedToggle: () => toggleFieldPin(ReservationField.period),
        })
    }

    requestDetailsItems.push(
        {
            label: t('asset.purpose'),
            value: getPurposeLabel(reservation.purpose),
            valueTestId: "reservation-purpose",
            pinned: pinnedFields?.includes(ReservationField.purpose),
            onPinnedToggle: () => toggleFieldPin(ReservationField.purpose),
        },
        {
            label: t('common.warehouse'),
            value: warehouseDescription || noValue,
            valueTestId: "reservation-warehouse",
            pinned: pinnedFields?.includes(ReservationField.warehouse),
            onPinnedToggle: () => toggleFieldPin(ReservationField.warehouse),
        },
        {
            label: t('profitCenters.profitCenter'),
            value: profitCenter?.description || noValue,
            valueTestId: "reservation-profit-center",
            pinned: pinnedFields?.includes(ReservationField.profitCenter),
            onPinnedToggle: () => toggleFieldPin(ReservationField.profitCenter),
        },
    )

    const requestDetailsSubsections: ISummarySection<IListItem>[] = []

    // Editable comments subsection
    requestDetailsSubsections.push({
        title: t('reservation.comments'),
        toolbar: commentToolbar,
        items: [{
            value: (isEditingComment && editingContext === 'section') ? (
                <Input
                    type="textarea"
                    value={commentValue}
                    onChange={(e) => setCommentValue(e.target.value)}
                    onBlur={handleBlurComment}
                    autoFocus
                    placeholder={t('reservation.commentsPlaceholder')}
                    maxLength={2500}
                />
            ) : (
                <div className="mb-2">{reservation.comment || <NoValue>{t('reservation.noComments')}</NoValue>}</div>
            ),
            type: ListItemType.verticalSection,
            valueTestId: 'reservation-comments',
            pinned: pinnedFields?.includes(ReservationField.comments),
            onPinnedToggle: () => toggleFieldPin(ReservationField.comments),
        }]
    })

    // Rent-to-Buy subsection
    if (reservation.hasRentToBuySummarySection) {
        const rentToBuyItems: IListItem[] = []

        if (reservation.purpose === Purpose.rent) {
            rentToBuyItems.push({
                label: t('reservation.markAsRentToBuy'),
                value: reservation.rentToBuy.mark ? t('common.yes') : t('common.no'),
                valueTestId: "reservation-mark-as-rent-to-buy",
            })
        }

        rentToBuyItems.push({
            label: t('reservation.relatedRequestForRentToBuy'),
            value: (isLoadingRentToBuyRelatedReservation
                ? <NoValue>{t('common.loading')}</NoValue>
                : rentToBuyRelatedReservation?.number) ?? noValue,
            valueTestId: "reservation-related-request-for-sale",
        })

        if (reservation.hasPricingRentToByFields && reservation.rentToBuy.mark) {
            rentToBuyItems.push({
                label: t('reservation.netValueAtBeginningOfContract'),
                value: typeof reservation.rentToBuy.netValue === "number"
                    ? t('common.amountWithCurrency', { count: reservation.rentToBuy.netValue, currency })
                    : noValue,
            })
            rentToBuyItems.push({
                label: t('reservation.interestPercentage'),
                value: typeof reservation.rentToBuy.interestPercentage === "number"
                    ? `${formatNumber(reservation.rentToBuy.interestPercentage)}%`
                    : noValue,
            })
        }

        if (reservation.purpose === Purpose.rent) {
            rentToBuyItems.push({
                label: t('reservation.changeAssetTypesToRentToBuy'),
                value: <>
                    {reservation.rentToBuy.changeAssetTypeOnRelease ? t('common.yes') : t('common.no')}
                    {!!changeAssetTypeLastUserEmail && !!reservation.rentToBuy.changeAssetTypeLastUpdateDate &&
                        <Text type="pSmallest" className="color-grey-500">
                            {t("reservation.lastChangedBy")}: {changeAssetTypeLastUserEmail},{" "}
                            <span className="white-space-nowrap">
                                {dayjs(reservation.rentToBuy.changeAssetTypeLastUpdateDate).format(t("config.dateLongTimeFormat"))}
                            </span>
                        </Text>
                    }
                </>,
                valueTestId: "reservation-change-asset-types-to-rent-to-buy",
            })
        }
        requestDetailsSubsections.push({
            title: t('reservation.rentToBuy'),
            items: rentToBuyItems,
        })
    }

    // We keep pinned rows in place; collection for top summary happens after all sections built.
    const filteredRequestDetailsItems = requestDetailsItems
    details.push({
        title: <div className="mt-2">{t('reservation.requestDetails')}</div>,
        items: filteredRequestDetailsItems,
        subSections: requestDetailsSubsections,
        editLink: canEdit
            ? Routes.reservationEdit.getPath(
                { reservationId: reservation.id },
                { backUrl: location.pathname + location.search, step: ReservationStep.requestDetails }
            )
            : undefined
    })

    // Customer Details
    const customerDetailsItems: IListItem[] = []
    if (reservation.purpose !== Purpose.relocation) {
        customerDetailsItems.push({
            label: t('reservation.customerNumber'),
            value: reservation.customer.number
                ? <CustomerNumber
                    customerNumber={reservation.customer.number}
                    blockedTooltipText={t('common.customerNumberBlockedNoInvoices')}
                    blocked={reservation.customer.blocked}
                />
                : noValue,
            valueTestId: "reservation-customer-number",
            pinned: pinnedFields?.includes(ReservationField.customerNumber),
            onPinnedToggle: () => toggleFieldPin(ReservationField.customerNumber),
        })
    }
    customerDetailsItems.push(
        {
            label: t('reservation.customerName'),
            value: reservation.customer.name || noValue,
            valueTestId: "reservation-customer-name",
            pinned: pinnedFields?.includes(ReservationField.customerName),
            onPinnedToggle: () => toggleFieldPin(ReservationField.customerName),
        },
        {
            label: t('reservation.invoiceAddress'),
            value: !!reservation.customer.address && !reservation.customer.address.isEmpty()
                ? reservation.customer.address.print()
                : noValue,
            valueTestId: "reservation-customer-address",
            pinned: pinnedFields?.includes(ReservationField.customerAddress),
            onPinnedToggle: () => toggleFieldPin(ReservationField.customerAddress),
        },
        {
            label: t('reservation.requestLanguage'),
            value: language?.title || noValue,
            valueTestId: "reservation-language",
            pinned: pinnedFields?.includes(ReservationField.language),
            onPinnedToggle: () => toggleFieldPin(ReservationField.language),
        },
        {
            label: t('reservation.requestCurrency'),
            value: reservation.requestCurrency || noValue,
            valueTestId: "reservation-currency",
            pinned: pinnedFields?.includes(ReservationField.currency),
            onPinnedToggle: () => toggleFieldPin(ReservationField.currency),
        },
        {
            label: t('reservation.crmReferenceNumber'),
            value: reservation.crmReferenceNumber || noValue,
            valueTestId: "reservation-crm-reference-number",
            pinned: pinnedFields?.includes(ReservationField.crmReferenceNumber),
            onPinnedToggle: () => toggleFieldPin(ReservationField.crmReferenceNumber),
        },
        {
            label: t('reservation.contactPerson'),
            value: reservation.customer.customerContact?.name || noValue,
            valueTestId: "reservation-contact-person",
            pinned: pinnedFields?.includes(ReservationField.contactPerson),
            onPinnedToggle: () => toggleFieldPin(ReservationField.contactPerson),
        },
        {
            label: t('reservation.telephoneNumber'),
            value: reservation.customer.customerContact?.telephone || noValue,
            valueTestId: "reservation-customer-phone",
            pinned: pinnedFields?.includes(ReservationField.customerPhone),
            onPinnedToggle: () => toggleFieldPin(ReservationField.customerPhone),
        },
        {
            label: t('reservation.emailAddress'),
            value: reservation.customer.customerContact?.email || noValue,
            valueTestId: "reservation-customer-email",
            pinned: pinnedFields?.includes(ReservationField.customerEmail),
            onPinnedToggle: () => toggleFieldPin(ReservationField.customerEmail),
        },
    )

    if (reservation.hasSalesAndCustomerOrderInfo || reservation.hasSalesAdminField) {
        if (reservation.hasSalesAndCustomerOrderInfo) {
            customerDetailsItems.push({
                label: t('reservation.salesPerson'),
                value: (salesPerson ? salesPerson.print() : (salesPerson === null ? loading : noValue)) || noValue,
                valueTestId: "reservation-sales-person",
                pinned: pinnedFields?.includes(ReservationField.salesPerson),
                onPinnedToggle: () => toggleFieldPin(ReservationField.salesPerson),
            })
        }
        customerDetailsItems.push({
            label: t('reservation.salesAdministrator'),
            value: (salesAdmin ? salesAdmin.print() : (salesAdmin === null ? loading : noValue)) || noValue,
            valueTestId: "reservation-sales-admin",
            pinned: pinnedFields?.includes(ReservationField.salesAdmin),
            onPinnedToggle: () => toggleFieldPin(ReservationField.salesAdmin),
        })
        if (reservation.hasSalesAndCustomerOrderInfo) {
            customerDetailsItems.push(
                {
                    label: t('reservation.customerOrderNumber'),
                    value: reservation.customerOrderNumber || noValue,
                    valueTestId: "reservation-customer-order-number",
                    pinned: pinnedFields?.includes(ReservationField.customerOrderNumber),
                    onPinnedToggle: () => toggleFieldPin(ReservationField.customerOrderNumber),
                },
                {
                    label: t('reservation.customerOrderDate'),
                    value: reservation.customerOrderDate.format(t("config.dateLongFormat")),
                    valueTestId: "reservation-customer-order-date",
                    pinned: pinnedFields?.includes(ReservationField.customerOrderDate),
                    onPinnedToggle: () => toggleFieldPin(ReservationField.customerOrderDate),
                },
            )
        }
    }

    if (isCommissionDisplayed) {
        customerDetailsItems.push({
            label: t('commission.commission'),
            value: reservation.commission === null ? t('commission.noCommission') : `${formatNumber(reservation.commission, 2)}%`,
            valueTestId: "reservation-commission",
            pinned: pinnedFields?.includes(ReservationField.commission),
            onPinnedToggle: () => toggleFieldPin(ReservationField.commission),
        })
    }

    const filteredCustomerDetailsItems = customerDetailsItems
    details.push({
        title: t('reservation.customerDetails'),
        items: filteredCustomerDetailsItems,
        editLink: canEdit
            ? Routes.reservationEdit.getPath(
                { reservationId: reservation.id },
                { backUrl: location.pathname + location.search, step: ReservationStep.customerDetails }
            )
            : undefined
    })

    // Delivery (with map at top)
    const deliveryItems: IListItem[] = [
        {
            value: renderMap(),
            type: ListItemType.verticalSection,
        },
        {
            label: t('reservation.address'),
            value: !reservation.address.isEmpty() ||
                (reservation.purpose !== Purpose.relocation && (reservation.recipientName || reservation.constructionSite))
                ? <div className="word-break-break-word">
                    {reservation.purpose === Purpose.relocation
                        ? (addressWarehouseDescription === null
                            ? <NoValue>{t('common.loading')}</NoValue>
                            : (addressWarehouseDescription || ''))
                        : ''}
                    <div className={addressWarehouseDescription ? 'font-regular' : undefined}>
                        {reservation.purpose !== Purpose.relocation && reservation.recipientName ? `${reservation.recipientName}\n` : ''}
                        {reservation.purpose !== Purpose.relocation && reservation.constructionSite ? `${reservation.constructionSite}\n` : ''}
                        {reservation.address.print()}
                    </div>
                </div>
                : noValue,
            valueTestId: "reservation-address",
            pinned: pinnedFields?.includes(ReservationField.address),
            onPinnedToggle: () => toggleFieldPin(ReservationField.address),
        },
        {
            label: t('common.coordinates'),
            value: `${reservation.location.lat}, ${reservation.location.lng}`,
            valueTestId: "reservation-coordinates",
            pinned: pinnedFields?.includes(ReservationField.coordinates),
            onPinnedToggle: () => toggleFieldPin(ReservationField.coordinates),
        },
    ]

    if (reservation.hasTargetCountryField) {
        deliveryItems.push({
            label: t('reservation.targetCountry'),
            value: targetCountry?.name || noValue,
            valueTestId: "reservation-target-country",
            pinned: pinnedFields?.includes('reservation-target-country'),
            onPinnedToggle: () => toggleFieldPin('reservation-target-country'),
        })
    }

    deliveryItems.push(
        {
            label: t('shippingOrders.shippingInstructions'),
            value: reservation.shippingDetails || <NoValue>{t('common.noComments')}</NoValue>,
            type: ListItemType.verticalSection,
            valueTestId: "reservation-shipping-details",
            pinned: pinnedFields?.includes('reservation-shipping-details'),
            onPinnedToggle: () => toggleFieldPin('reservation-shipping-details'),
        },
        {
            label: t('reservation.readyForShipping'),
            value: t(reservation.readyForShipping ? 'common.yes' : 'common.no'),
            valueTestId: "reservation-forwarded-to-shipping",
            pinned: pinnedFields?.includes('reservation-forwarded-to-shipping'),
            onPinnedToggle: () => toggleFieldPin('reservation-forwarded-to-shipping'),
        },
    )

    deliveryItems.push(
        {
            label: t('reservation.contactPersonAtDeliveryAddress'),
            value: reservation.personInCharge || noValue,
            valueTestId: "reservation-delivery-contact-person",
            pinned: pinnedFields?.includes('reservation-delivery-contact-person'),
            onPinnedToggle: () => toggleFieldPin('reservation-delivery-contact-person'),
        },
        {
            label: t('reservation.contactPersonAtDeliveryAddressPhone'),
            value: reservation.personInChargePhone || noValue,
            valueTestId: "reservation-delivery-contact-phone",
            pinned: pinnedFields?.includes('reservation-delivery-contact-phone'),
            onPinnedToggle: () => toggleFieldPin('reservation-delivery-contact-phone'),
        },
    )

    if (reservation.hasDeliveryTerms) {
        deliveryItems.push({
            label: t('reservation.termsOfDelivery'),
            value: deliveryTerm?.print() || noValue,
            valueTestId: "reservation-terms-of-delivery",
            pinned: pinnedFields?.includes('reservation-terms-of-delivery'),
            onPinnedToggle: () => toggleFieldPin('reservation-terms-of-delivery'),
        })
    }

    const deliverySubsections: ISummarySection<IListItem>[] = [{
        title: t('reservation.deliveryNoteText'),
        items: [
            {
                label: t('reservation.headerSection'),
                value: reservation.deliveryNoteHeaderText
                    ? <div className="font-regular">
                        <ButtonLinkTooltip
                            label={t('reservation.showText')}
                            tooltipProps={{
                                whiteSpace: "pre-wrap",
                                content: reservation.deliveryNoteHeaderText,
                                maxWidth: "800px",
                            }}
                        />
                    </div>
                    : noText,
                valueTestId: "reservation-delivery-note-header",
                pinned: pinnedFields?.includes('reservation-delivery-note-header'),
                onPinnedToggle: () => toggleFieldPin('reservation-delivery-note-header'),
            },
            {
                label: t('reservation.footerSection'),
                value: reservation.deliveryNoteFooterText
                    ? <div className="font-regular">
                        <ButtonLinkTooltip
                            label={t('reservation.showText')}
                            tooltipProps={{
                                whiteSpace: "pre-wrap",
                                content: reservation.deliveryNoteFooterText,
                                maxWidth: "800px",
                            }}
                        />
                    </div>
                    : noText,
                valueTestId: "reservation-delivery-note-footer",
                pinned: pinnedFields?.includes('reservation-delivery-note-footer'),
                onPinnedToggle: () => toggleFieldPin('reservation-delivery-note-footer'),
            },
        ],
    }]

    const filteredDeliveryItems = deliveryItems
    const filteredDeliverySubsections = deliverySubsections
    details.push({
        title: t('reservation.delivery'),
        items: filteredDeliveryItems,
        subSections: filteredDeliverySubsections,
        editLink: canEdit
            ? Routes.reservationEdit.getPath(
                { reservationId: reservation.id },
                { backUrl: location.pathname + location.search, step: ReservationStep.delivery }
            )
            : undefined
    })

    // Pricing
    if (reservation.pricingDetails) {
        const pricingItems: IListItem[] = []
        const pricingSubSections: ISummarySection<IListItem>[] = []

        if (reservation.hasPricing && reservation.invoicing) {
            // VAT
            pricingSubSections.push({
                title: t('reservation.valueAddedTax'),
                items: [
                    {
                        label: t('reservation.orderType'),
                        value: (orderType ? orderType.print() : (orderType === null ? loading : noValue)) || noValue,
                        valueTestId: 'reservation-order-type',
                        pinned: pinnedFields?.includes('reservation-order-type'),
                        onPinnedToggle: () => toggleFieldPin('reservation-order-type'),
                    },
                    {
                        label: t('reservation.countryOfDeparture'),
                        value: (originCountry ? originCountry.name : (originCountry === null ? loading : noValue)) || noValue,
                        valueTestId: 'reservation-origin-country',
                        pinned: pinnedFields?.includes('reservation-origin-country'),
                        onPinnedToggle: () => toggleFieldPin('reservation-origin-country'),
                    },
                ]
            })

            // Invoicing settings
            const invoicingSettingsItems: IListItem[] = []
            if (reservation.pricingDetails.hasInvoicingSettings && reservation.pricingDetails.invoicingType !== null) {
                invoicingSettingsItems.push({
                    label: t('reservation.createInvoice'),
                    value: t(
                        reservation.pricingDetails.invoicingType === InvoicingType.before
                            ? t('reservation.beforeInvoicePeriod')
                            : t('reservation.afterInvoicePeriod')
                    ),
                    valueTestId: 'reservation-create-invoice',
                    pinned: pinnedFields?.includes('reservation-create-invoice'),
                    onPinnedToggle: () => toggleFieldPin('reservation-create-invoice'),
                })
            }
            if (reservation.hasInvoicingBySettings) {
                invoicingSettingsItems.push({
                    label: t('reservation.invoicingBy'),
                    value: <>
                        {reservation.pricingDetails?.invoiceRateCalculationType
                            ? t(`invoiceRateCalculationType.${reservation.pricingDetails?.invoiceRateCalculationType}`)
                            : null}
                        <Text>{t(reservation.workingDaysCalendarId ? 'reservation.workingDays' : 'reservation.calendarDays')}</Text>
                        {reservation.workingDaysCalendarId &&
                            <Text>
                                {`${workingDaysCalendarsMap.get(reservation.workingDaysCalendarId)?.description} (${t('common.xDays', {
                                    count: reservation.workingDaysPerMonth || workingDaysCalendarsMap.get(reservation.workingDaysCalendarId)?.workingDaysPerMonth
                                })})`}
                            </Text>
                        }
                    </>,
                    valueTestId: 'reservation-invoicing-by',
                    pinned: pinnedFields?.includes('reservation-invoicing-by'),
                    onPinnedToggle: () => toggleFieldPin('reservation-invoicing-by'),
                })
            }
            if (reservation.pricingDetails.hasInvoicingSettings && reservation.pricingDetails.automaticInvoicing !== null) {
                invoicingSettingsItems.push({
                    label: t('reservation.createInvoiceAutomatically'),
                    value: t(reservation.pricingDetails.automaticInvoicing ? t('common.yes') : t('common.no')),
                    valueTestId: 'reservation-automatic-invoicing',
                    pinned: pinnedFields?.includes('reservation-automatic-invoicing'),
                    onPinnedToggle: () => toggleFieldPin('reservation-automatic-invoicing'),
                })
            }
            if (reservation.pricingDetails.invoiceBeforeShipping !== null) {
                invoicingSettingsItems.push({
                    label: t('reservation.createFirstInvoiceBeforeShipping'),
                    value: t(reservation.pricingDetails.invoiceBeforeShipping ? t('common.yes') : t('common.no')),
                    valueTestId: 'reservation-first-invoice-before-shipping',
                    pinned: pinnedFields?.includes('reservation-first-invoice-before-shipping'),
                    onPinnedToggle: () => toggleFieldPin('reservation-first-invoice-before-shipping'),
                })
            }
            invoicingSettingsItems.push({
                label: t('reservation.invoiceBlock'),
                value: <>
                    {t(reservation.pricingDetails.invoiceBlocked ? 'common.yes' : 'common.no')}
                    {reservation.pricingDetails.invoiceBlocked && !!reservation.pricingDetails.invoiceBlockedReason &&
                        <div className='font-regular'>{reservation.pricingDetails.invoiceBlockedReason}</div>}
                    {reservation.pricingDetails.invoiceBlocked && !!reservation.pricingDetails.invoiceBlockedDate && !!invoiceBlockedByEmail &&
                        <Text type='pSmallest' className='color-grey-500'>
                            {t('reservation.activatedOnBy', {
                                user: invoiceBlockedByEmail,
                                date: reservation.pricingDetails.invoiceBlockedDate.format(t('config.dateLongTimeFormat'))
                            })}
                        </Text>}
                </>,
                valueTestId: 'reservation-invoice-block',
                pinned: pinnedFields?.includes('reservation-invoice-block'),
                onPinnedToggle: () => toggleFieldPin('reservation-invoice-block'),
            })
            pricingSubSections.push({ title: t('reservation.invoicingSettings'), items: invoicingSettingsItems })

            // Interval
            if (reservation.hasInvoiceIntervalSettings) {
                pricingSubSections.push({
                    title: t('reservation.invoiceInterval'),
                    items: [{
                        label: t('reservation.intervalRule'),
                        value: reservation.invoiceIntervalRule
                            ? `${t(`invoiceIntervalRule.${reservation.invoiceIntervalRule}`)}${reservation.invoiceIntervalRuleCustom ? ` (${t('invoiceIntervalRule.custom')})` : ''}`
                            : noValue,
                        valueTestId: 'reservation-invoice-interval-rule',
                        pinned: pinnedFields?.includes('reservation-invoice-interval-rule'),
                        onPinnedToggle: () => toggleFieldPin('reservation-invoice-interval-rule'),
                    }]
                })
            }

            // Downtimes
            if (reservation.downtimePeriods.length > 0) {
                pricingSubSections.push({
                    title: `${t('reservation.downtimes')} (${t('reservation.xDaysTotal', { count: reservation.getDowntimePeriodsDaysTotal() })})`,
                    items: reservation.downtimePeriods.map((dp, idx) => ({
                        label: dp.description,
                        value: dp.printDates(),
                        valueTestId: `reservation-downtime-${idx}`,
                        pinned: pinnedFields?.includes(`reservation-downtime-${idx}`),
                        onPinnedToggle: () => toggleFieldPin(`reservation-downtime-${idx}`),
                    }))
                })
            }

            // Machine insurance
            pricingSubSections.push({
                title: t('reservation.machineInsurance'),
                items: [
                    {
                        label: t('reservation.machineInsuranceIncluded'),
                        value: reservation.pricingDetails.machineInsuranceIncluded ? t('common.yes') : t('common.no'),
                        valueTestId: 'reservation-machine-insurance-included',
                        pinned: pinnedFields?.includes('reservation-machine-insurance-included'),
                        onPinnedToggle: () => toggleFieldPin('reservation-machine-insurance-included'),
                    },
                    ...(reservation.pricingDetails.machineInsuranceIncluded ? [{
                        label: t('reservation.excessMinimum'),
                        value: typeof reservation.pricingDetails.machineInsuranceExcess === 'number'
                            ? t('common.amountWithCurrency', { count: reservation.pricingDetails.machineInsuranceExcess, currency })
                            : noValue,
                        valueTestId: 'reservation-machine-insurance-excess',
                        pinned: pinnedFields?.includes('reservation-machine-insurance-excess'),
                        onPinnedToggle: () => toggleFieldPin('reservation-machine-insurance-excess'),
                    }] : [])
                ]
            })

            // Invoice positions
            pricingSubSections.push({
                title: t('reservation.invoicePositions'),
                items: reservation.pricingDetails.invoicePositions && reservation.pricingDetails.invoicePositions.length > 0
                    ? [
                        ...reservation.pricingDetails.invoicePositions.map((invoicePosition, idx) => ({
                            label: `${invoicePosition.description}${invoicePosition.type && reservation.purpose !== Purpose.sale
                                ? ` (${getInvoicePositionTypeLabel(invoicePosition.type)})`
                                : ''}`,
                            value: typeof invoicePosition.amount === 'number' && currency
                                ? t('common.amountWithCurrency', { count: invoicePosition.amount, currency })
                                : noValue,
                            valueTestId: `reservation-invoice-position-${idx}`,
                            pinned: pinnedFields?.includes(`reservation-invoice-position-${idx}`),
                            onPinnedToggle: () => toggleFieldPin(`reservation-invoice-position-${idx}`),
                        })),
                        {
                            label: totalPrices.map(tp => `${tp.label}\n`),
                            value: totalPrices.map(tp =>
                                `${currency
                                    ? t('common.amountWithCurrency', { count: tp.value, currency })
                                    : formatNumber(tp.value, 2, 2)}\n`
                            ),
                            isOverlined: true,
                            valueTestId: 'reservation-invoice-total-prices',
                            pinned: pinnedFields?.includes('reservation-invoice-total-prices'),
                            onPinnedToggle: () => toggleFieldPin('reservation-invoice-total-prices'),
                        }
                    ]
                    : [{
                        value: <NoValue>{t('reservation.noInvoicePositions')}</NoValue>,
                        valueTestId: 'reservation-no-invoice-positions',
                        pinned: pinnedFields?.includes('reservation-no-invoice-positions'),
                        onPinnedToggle: () => toggleFieldPin('reservation-no-invoice-positions'),
                    }]
            })

            // (Sale only) Order confirmation text
            if (reservation.purpose === Purpose.sale) {
                pricingSubSections.push({
                    title: t('reservation.orderConfirmationText'),
                    items: [
                        {
                            label: t('reservation.headerSection'),
                            value: reservation.pricingDetails.headerSectionConfirmationText
                                ? <ButtonLinkTooltip
                                    label={t('reservation.showText')}
                                    tooltipProps={{
                                        whiteSpace: 'pre-wrap',
                                        content: reservation.pricingDetails.headerSectionConfirmationText,
                                        maxWidth: '800px'
                                    }}
                                /> : noText,
                            valueTestId: 'reservation-order-confirmation-header',
                            pinned: pinnedFields?.includes('reservation-order-confirmation-header'),
                            onPinnedToggle: () => toggleFieldPin('reservation-order-confirmation-header'),
                        },
                        {
                            label: t('reservation.footerSection'),
                            value: reservation.pricingDetails.footerSectionConfirmationText
                                ? <ButtonLinkTooltip
                                    label={t('reservation.showText')}
                                    tooltipProps={{
                                        whiteSpace: 'pre-wrap',
                                        content: reservation.pricingDetails.footerSectionConfirmationText,
                                        maxWidth: '800px'
                                    }}
                                /> : noText,
                            valueTestId: 'reservation-order-confirmation-footer',
                            pinned: pinnedFields?.includes('reservation-order-confirmation-footer'),
                            onPinnedToggle: () => toggleFieldPin('reservation-order-confirmation-footer'),
                        },
                    ]
                })
            }

            // Invoice text
            pricingSubSections.push({
                title: t('reservation.invoiceText'),
                items: [
                    {
                        label: t('reservation.headerSection'),
                        value: reservation.pricingDetails.invoiceHeaderText
                            ? <ButtonLinkTooltip
                                label={t('reservation.showText')}
                                tooltipProps={{
                                    whiteSpace: "pre-wrap",
                                    content: applyLineBreakAfterRule(
                                        RESERVATION_INVOICE_TEXT_LINE_BREAK_AFTER,
                                        reservation.pricingDetails.invoiceHeaderText
                                    ),
                                    maxWidth: "800px",
                                }}
                            />
                            : noText,
                        valueTestId: 'reservation-invoice-header-text',
                        pinned: pinnedFields?.includes('reservation-invoice-header-text'),
                        onPinnedToggle: () => toggleFieldPin('reservation-invoice-header-text'),
                    },
                    {
                        label: t('reservation.footerSection'),
                        value: reservation.pricingDetails.invoiceFooterText
                            ? <ButtonLinkTooltip
                                label={t('reservation.showText')}
                                tooltipProps={{
                                    whiteSpace: "pre-wrap",
                                    content: applyLineBreakAfterRule(
                                        RESERVATION_INVOICE_TEXT_LINE_BREAK_AFTER,
                                        reservation.pricingDetails.invoiceFooterText
                                    ),
                                    maxWidth: "800px",
                                }}
                            />
                            : noText,
                        valueTestId: 'reservation-invoice-footer-text',
                        pinned: pinnedFields?.includes('reservation-invoice-footer-text'),
                        onPinnedToggle: () => toggleFieldPin('reservation-invoice-footer-text'),
                    },
                ]
            })

            pricingItems.push({
                label: t('reservation.paymentConditions'),
                value: (paymentTerm ? paymentTerm.print() : (paymentTerm === null ? loading : noValue)) || noValue,
                valueTestId: 'reservation-payment-conditions',
                pinned: pinnedFields?.includes('reservation-payment-conditions'),
                onPinnedToggle: () => toggleFieldPin('reservation-payment-conditions'),
            })
        } else {
            pricingItems.push({
                label: t('reservation.noInvoicesForRequest'),
                valueTestId: 'reservation-no-invoices',
            })
        }

        details.push({
            title: t('reservation.pricing'),
            items: pricingItems,
            subSections: pricingSubSections,
            editLink: canEdit
                ? Routes.reservationEdit.getPath(
                    { reservationId: reservation.id },
                    { backUrl: location.pathname + location.search, step: ReservationStep.pricing }
                )
                : undefined
        })
    }

    // Build pinned block after sections fully assembled
    // IMPORTANT: Order must reflect the order in which the user pinned the rows (append-at-bottom behaviour),
    // not the order in which they appear in the sidebar sections. We therefore first index all items by id
    // and then map over pinnedFields to produce the final ordered list. This mirrors asset sidebar behaviour.
    if (pinnedFields && pinnedFields.length) {
        const itemsById: Record<string, IListItem> = {}
        const seen = new Set<string>() // ensure we keep only the first occurrence encountered in the sections
        const indexItems = (items: IListItem[]) => {
            for (const it of items) {
                if (!it.valueTestId) continue
                if (seen.has(it.valueTestId)) continue
                seen.add(it.valueTestId)
                itemsById[it.valueTestId] = it
            }
        }
        for (const section of details) {
            indexItems(section.items)
            section.subSections?.forEach(ss => indexItems(ss.items))
        }

        const pinnedItemsForBlock: IListItem[] = []
        for (const id of pinnedFields) {
            const original = itemsById[id]
            if (!original) continue // skip stale ids (e.g., dynamic rows removed)
            if (id === ReservationField.comments) {
                const isPinnedEditing = isEditingComment && editingContext === 'pinned'
                pinnedItemsForBlock.push({
                    ...original,
                    label: t('reservation.comments'),
                    value: isPinnedEditing ? (
                        <Input
                            type="textarea"
                            value={commentValue}
                            onChange={(e) => setCommentValue(e.target.value)}
                            onBlur={handleBlurComment}
                            autoFocus
                            placeholder={t('reservation.commentsPlaceholder')}
                        />
                    ) : (
                        <div className="mb-2">{reservation.comment || <NoValue>{t('reservation.noComments')}</NoValue>}</div>
                    ),
                    toolbar: (!isPinnedEditing && editingContext !== 'section') ? (
                        <Button
                            label={t('common.edit')}
                            theme="link"
                            onClick={() => startEditComment('pinned')}
                            disabled={editingContext === 'section'}
                        />
                    ) : (isPinnedEditing ? (
                        <div onPointerDown={onToolbarPointerDown}>
                            <Flex gap="0.8rem" alignItems="center">
                                <Button label={t('common.save')} theme="link" onClick={handleSaveComment} />
                                <div className="reservation-sidebar__toolbar-divider" />
                                <Button label={t('common.cancel')} theme="link" onClick={handleCancelComment} />
                            </Flex>
                        </div>
                    ) : undefined),
                    pinned: true,
                })
            } else {
                pinnedItemsForBlock.push({ ...original, pinned: true })
            }
        }

        if (pinnedItemsForBlock.length) {
            return (
                <>
                    <div className="px-1-5 py-3 bg-grey-50 round mb-4">
                        <Summary data={[{ items: pinnedItemsForBlock }]} />
                    </div>
                    <Summary data={details} />
                </>
            )
        }
    }
    return <Summary data={details} />
}

export default ReservationSidebarDetails
